import {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Link,
} from "react-router-dom";

import {
    AlertCircle,
    BadgeCheck,
    Clock,
    Upload,
} from "lucide-react";

import {
    toast,
} from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";

import {
    Button,
} from "@/components/ui/button";

import {
    Input,
} from "@/components/ui/input";

import {
    Label,
} from "@/components/ui/label";

import {
    RadioGroup,
    RadioGroupItem,
} from "@/components/ui/radio-group";


const EMPTY_FORM = {
    seller_type:
        "commercial_registration",

    identity_type:
        "national_id",

    identity_number: "",

    legal_name: "",

    business_name: "",

    activity_name: "",

    city: "",

    business_document_number: "",

    identity_file_id: "",

    business_document_file_id: "",

    vat_registered: false,

    vat_number: "",

    vat_certificate_file_id: "",

    bank_name: "",

    bank_account_name: "",

    iban: "",

    iban_certificate_file_id: "",
};


export default function Verification() {
    const {
        lang,
    } = useLang();

    const ar = lang === "ar";

    const [
        statusData,
        setStatusData,
    ] = useState(null);

    const [
        form,
        setForm,
    ] = useState(
        EMPTY_FORM
    );

    const [
        uploading,
        setUploading,
    ] = useState({});

    const [
        submitting,
        setSubmitting,
    ] = useState(false);


    const load = useCallback(
        async () => {
            try {
                const {
                    data,
                } = await api.get(
                    "/seller/verification"
                );

                setStatusData(data);

                if (data.application) {
                    const app =
                        data.application;

                    setForm({
                        seller_type:
                            app.seller_type
                            || "commercial_registration",

                        identity_type:
                            app.identity_type
                            || "national_id",

                        identity_number:
                            app.identity_number
                            || "",

                        legal_name:
                            app.legal_name
                            || "",

                        business_name:
                            app.business_name
                            || "",

                        activity_name:
                            app.activity_name
                            || "",

                        city:
                            app.city || "",

                        business_document_number:
                            app.business_document_number
                            || "",

                        identity_file_id:
                            app.identity_file_id
                            || "",

                        business_document_file_id:
                            app.business_document_file_id
                            || "",

                        vat_registered:
                            Boolean(
                                app.vat_registered
                            ),

                        vat_number:
                            app.vat_number
                            || "",

                        vat_certificate_file_id:
                            app.vat_certificate_file_id
                            || "",

                        bank_name:
                            app.bank_name
                            || "",

                        bank_account_name:
                            app.bank_account_name
                            || "",

                        iban:
                            app.iban || "",

                        iban_certificate_file_id:
                            app.iban_certificate_file_id
                            || "",
                    });
                }
            } catch (err) {
                toast.error(
                    formatApiError(
                        err.response?.data?.detail
                    )
                    || (
                        ar
                            ? "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u062d\u0627\u0644\u0629 \u0627\u0644\u0637\u0644\u0628"
                            : "Could not load application status"
                    )
                );
            }
        },
        [ar]
    );


    useEffect(() => {
        load();
    }, [load]);


    const setValue = (
        key,
        value
    ) => {
        setForm(
            (current) => ({
                ...current,
                [key]: value,
            })
        );
    };


    const uploadDocument = async (
        event,
        field
    ) => {
        const file =
            event.target.files?.[0];

        if (!file) {
            return;
        }

        setUploading(
            (current) => ({
                ...current,
                [field]: true,
            })
        );

        try {
            const fd =
                new FormData();

            fd.append(
                "file",
                file
            );

            const {
                data,
            } = await api.post(
                "/seller/verification/upload",
                fd
            );

            setValue(
                field,
                data.file_id
            );

            toast.success(
                ar
                    ? "\u062a\u0645 \u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f"
                    : "Document uploaded"
            );

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                )
                || (
                    ar
                        ? "\u062a\u0639\u0630\u0631 \u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f"
                        : "Document upload failed"
                )
            );

        } finally {
            setUploading(
                (current) => ({
                    ...current,
                    [field]: false,
                })
            );
        }
    };


    const submit = async (
        event
    ) => {
        event.preventDefault();

        const requiredFiles = [
            form.identity_file_id,
            form.business_document_file_id,
            form.iban_certificate_file_id,
        ];

        if (
            requiredFiles.some(
                (value) => !value
            )
        ) {
            toast.error(
                ar
                    ? "\u0627\u0644\u0631\u062c\u0627\u0621 \u0631\u0641\u0639 \u062c\u0645\u064a\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f\u0627\u062a \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629"
                    : "Please upload all required documents"
            );

            return;
        }

        if (
            form.vat_registered
            && (
                !form.vat_number
                || !form.vat_certificate_file_id
            )
        ) {
            toast.error(
                ar
                    ? "\u0627\u0644\u0631\u062c\u0627\u0621 \u0625\u062f\u062e\u0627\u0644 \u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0636\u0631\u064a\u0628\u064a \u0648\u0631\u0641\u0639 \u0634\u0647\u0627\u062f\u0629 \u0627\u0644\u0636\u0631\u064a\u0628\u0629"
                    : "VAT number and certificate are required"
            );

            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                ...form,

                vat_number:
                    form.vat_registered
                        ? form.vat_number
                        : null,

                vat_certificate_file_id:
                    form.vat_registered
                        ? form.vat_certificate_file_id
                        : null,
            };

            await api.post(
                "/seller/verification",
                payload
            );

            toast.success(
                ar
                    ? "\u062a\u0645 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0641\u062a\u062d \u0627\u0644\u0645\u062a\u062c\u0631 \u0644\u0644\u0645\u0631\u0627\u062c\u0639\u0629"
                    : "Seller application submitted for review"
            );

            await load();

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                )
                || (
                    ar
                        ? "\u062a\u0639\u0630\u0631 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0637\u0644\u0628"
                        : "Could not submit application"
                )
            );

        } finally {
            setSubmitting(false);
        }
    };


    if (!statusData) {
        return (
            <Layout>
                <div className="min-h-[40vh] grid place-items-center text-muted-foreground">
                    ...
                </div>
            </Layout>
        );
    }


    const status =
        statusData.status;

    const canEdit = [
        "onboarding_required",
        "changes_required",
        "rejected",
    ].includes(status);


    const sellerTypeLabel = {
        commercial_registration:
            ar
                ? "\u0633\u062c\u0644 \u062a\u062c\u0627\u0631\u064a"
                : "Commercial Registration",

        productive_family:
            ar
                ? "\u0623\u0633\u0631\u0629 \u0645\u0646\u062a\u062c\u0629"
                : "Productive Family",

        freelance:
            ar
                ? "\u0648\u062b\u064a\u0642\u0629 \u0639\u0645\u0644 \u062d\u0631"
                : "Freelance Document",
    };


    const businessNumberLabel =
        form.seller_type
        === "productive_family"
            ? (
                ar
                    ? "\u0631\u0642\u0645 \u0634\u0647\u0627\u062f\u0629 \u0627\u0644\u0623\u0633\u0631\u0629 \u0627\u0644\u0645\u0646\u062a\u062c\u0629"
                    : "Productive family certificate number"
            )
            : (
                form.seller_type
                === "freelance"
                    ? (
                        ar
                            ? "\u0631\u0642\u0645 \u0648\u062b\u064a\u0642\u0629 \u0627\u0644\u0639\u0645\u0644 \u0627\u0644\u062d\u0631"
                            : "Freelance document number"
                    )
                    : (
                        ar
                            ? "\u0631\u0642\u0645 \u0627\u0644\u0633\u062c\u0644 \u0627\u0644\u062a\u062c\u0627\u0631\u064a"
                            : "Commercial Registration number"
                    )
            );


    const UploadField = ({
        field,
        label,
    }) => (
        <div>
            <Label>
                {label}
                {" *"}
            </Label>

            <label className="mt-1.5 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-2xl p-5 cursor-pointer hover:border-terracotta transition-colors">
                <Upload className="w-4 h-4 text-terracotta" />

                <span className="text-sm text-muted-foreground">
                    {
                        uploading[field]
                            ? "..."
                            : form[field]
                                ? (
                                    ar
                                        ? "\u062a\u0645 \u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f \u2713"
                                        : "Document uploaded \u2713"
                                )
                                : (
                                    ar
                                        ? "\u0627\u062e\u062a\u0631 \u0645\u0644\u0641 PDF \u0623\u0648 \u0635\u0648\u0631\u0629"
                                        : "Choose PDF or image"
                                )
                    }
                </span>

                <input
                    type="file"
                    accept="image/*,.pdf"
                    disabled={
                        uploading[field]
                    }
                    onChange={
                        (event) =>
                            uploadDocument(
                                event,
                                field
                            )
                    }
                    className="hidden"
                />
            </label>
        </div>
    );


    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">

                <h1 className="text-3xl md:text-4xl font-black text-espresso">
                    {
                        ar
                            ? "\u0637\u0644\u0628 \u0641\u062a\u062d \u0645\u062a\u062c\u0631"
                            : "Seller Application"
                    }
                </h1>

                <p className="mt-2 text-sm text-muted-foreground">
                    {
                        ar
                            ? "\u0623\u0643\u0645\u0644 \u0628\u064a\u0627\u0646\u0627\u062a\u0643 \u0648\u0627\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f\u0627\u062a \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629. \u0644\u0646 \u064a\u062a\u0645 \u062a\u0641\u0639\u064a\u0644 \u0625\u0645\u0643\u0627\u0646\u064a\u0629 \u0641\u062a\u062d \u0627\u0644\u0645\u062a\u062c\u0631 \u0625\u0644\u0627 \u0628\u0639\u062f \u0645\u0631\u0627\u062c\u0639\u0629 \u0627\u0644\u0637\u0644\u0628."
                            : "Complete your details and upload the required documents. Store access will be enabled after approval."
                    }
                </p>


                {
                    status === "pending"
                    && (
                        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-2xl p-5 flex items-start gap-3">
                            <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0" />

                            <div>
                                <div className="font-bold text-espresso">
                                    {
                                        ar
                                            ? "\u0637\u0644\u0628\u0643 \u0642\u064a\u062f \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629"
                                            : "Your application is under review"
                                    }
                                </div>

                                <div className="text-sm text-muted-foreground mt-1">
                                    {
                                        ar
                                            ? "\u0644\u0627 \u064a\u0645\u0643\u0646 \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u0645\u062a\u062c\u0631 \u062d\u062a\u0649 \u062a\u062a\u0645 \u0645\u0648\u0627\u0641\u0642\u0629 \u0627\u0644\u0625\u062f\u0627\u0631\u0629."
                                            : "You cannot create your store until the application is approved."
                                    }
                                </div>
                            </div>
                        </div>
                    )
                }


                {
                    status === "approved"
                    && (
                        <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-5">
                            <div className="flex items-start gap-3">
                                <BadgeCheck className="w-6 h-6 text-green-600 flex-shrink-0" />

                                <div>
                                    <div className="font-bold text-espresso">
                                        {
                                            ar
                                                ? "\u062a\u0645\u062a \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0637\u0644\u0628\u0643"
                                                : "Your seller application was approved"
                                        }
                                    </div>

                                    <div className="text-sm text-muted-foreground mt-1">
                                        {
                                            ar
                                                ? "\u064a\u0645\u0643\u0646\u0643 \u0627\u0644\u0622\u0646 \u0627\u0644\u062f\u062e\u0648\u0644 \u0625\u0644\u0649 \u0644\u0648\u062d\u0629 \u0627\u0644\u0628\u0627\u0626\u0639 \u0648\u0625\u0646\u0634\u0627\u0621 \u0645\u062a\u062c\u0631\u0643."
                                                : "You can now access the seller dashboard and create your store."
                                        }
                                    </div>
                                </div>
                            </div>

                            <Button
                                asChild
                                className="mt-4 rounded-full bg-terracotta text-white"
                            >
                                <Link to="/seller">
                                    {
                                        ar
                                            ? "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0644\u0648\u062d\u0629 \u0627\u0644\u0628\u0627\u0626\u0639"
                                            : "Go to seller dashboard"
                                    }
                                </Link>
                            </Button>
                        </div>
                    )
                }


                {
                    (
                        status === "changes_required"
                        || status === "rejected"
                    )
                    && (
                        <div className="mt-6 bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3">
                            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />

                            <div>
                                <div className="font-bold text-espresso">
                                    {
                                        status
                                        === "changes_required"
                                            ? (
                                                ar
                                                    ? "\u064a\u0644\u0632\u0645 \u062a\u0639\u062f\u064a\u0644 \u0627\u0644\u0637\u0644\u0628"
                                                    : "Changes are required"
                                            )
                                            : (
                                                ar
                                                    ? "\u0644\u0645 \u064a\u062a\u0645 \u0642\u0628\u0648\u0644 \u0627\u0644\u0637\u0644\u0628"
                                                    : "Application rejected"
                                            )
                                    }
                                </div>

                                {
                                    statusData.application?.admin_note
                                    && (
                                        <div className="text-sm text-muted-foreground mt-2">
                                            {
                                                statusData.application.admin_note
                                            }
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    )
                }


                {
                    canEdit
                    && (
                        <form
                            onSubmit={submit}
                            className="mt-8 bg-white border border-border rounded-3xl p-6 md:p-8 space-y-8"
                            data-testid="seller-verification-form"
                        >

                            <section className="space-y-4">
                                <h2 className="text-lg font-black text-espresso">
                                    {
                                        ar
                                            ? "1. \u0646\u0648\u0639 \u0627\u0644\u0628\u0627\u0626\u0639"
                                            : "1. Seller type"
                                    }
                                </h2>

                                <RadioGroup
                                    value={
                                        form.seller_type
                                    }
                                    onValueChange={
                                        (value) =>
                                            setValue(
                                                "seller_type",
                                                value
                                            )
                                    }
                                    className="grid md:grid-cols-3 gap-3"
                                >
                                    {
                                        Object.entries(
                                            sellerTypeLabel
                                        ).map(
                                            ([
                                                value,
                                                label,
                                            ]) => (
                                                <label
                                                    key={value}
                                                    className={`p-4 rounded-xl border-2 cursor-pointer ${
                                                        form.seller_type
                                                        === value
                                                            ? "border-terracotta bg-terracotta/5"
                                                            : "border-border"
                                                    }`}
                                                >
                                                    <RadioGroupItem
                                                        value={value}
                                                        className="sr-only"
                                                    />

                                                    <span className="font-bold text-sm">
                                                        {label}
                                                    </span>
                                                </label>
                                            )
                                        )
                                    }
                                </RadioGroup>
                            </section>


                            <section className="space-y-4">
                                <h2 className="text-lg font-black text-espresso">
                                    {
                                        ar
                                            ? "2. \u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0647\u0648\u064a\u0629"
                                            : "2. Identity details"
                                    }
                                </h2>

                                <RadioGroup
                                    value={
                                        form.identity_type
                                    }
                                    onValueChange={
                                        (value) =>
                                            setValue(
                                                "identity_type",
                                                value
                                            )
                                    }
                                    className="flex gap-3"
                                >
                                    {
                                        [
                                            {
                                                value:
                                                    "national_id",

                                                label:
                                                    ar
                                                        ? "\u0647\u0648\u064a\u0629 \u0648\u0637\u0646\u064a\u0629"
                                                        : "National ID",
                                            },
                                            {
                                                value:
                                                    "iqama",

                                                label:
                                                    ar
                                                        ? "\u0625\u0642\u0627\u0645\u0629"
                                                        : "Iqama",
                                            },
                                        ].map(
                                            (item) => (
                                                <label
                                                    key={
                                                        item.value
                                                    }
                                                    className={`flex-1 p-3 rounded-xl border-2 cursor-pointer ${
                                                        form.identity_type
                                                        === item.value
                                                            ? "border-terracotta bg-terracotta/5"
                                                            : "border-border"
                                                    }`}
                                                >
                                                    <RadioGroupItem
                                                        value={
                                                            item.value
                                                        }
                                                        className="sr-only"
                                                    />

                                                    <span className="font-bold text-sm">
                                                        {
                                                            item.label
                                                        }
                                                    </span>
                                                </label>
                                            )
                                        )
                                    }
                                </RadioGroup>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0643\u0627\u0645\u0644 / \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a"
                                                    : "Full / legal name"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.legal_name
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "legal_name",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>

                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0631\u0642\u0645 \u0627\u0644\u0647\u0648\u064a\u0629 / \u0627\u0644\u0625\u0642\u0627\u0645\u0629"
                                                    : "ID / Iqama number"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.identity_number
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "identity_number",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>
                                </div>

                                <UploadField
                                    field="identity_file_id"
                                    label={
                                        ar
                                            ? "\u0635\u0648\u0631\u0629 \u0627\u0644\u0647\u0648\u064a\u0629 / \u0627\u0644\u0625\u0642\u0627\u0645\u0629"
                                            : "ID / Iqama document"
                                    }
                                />
                            </section>


                            <section className="space-y-4">
                                <h2 className="text-lg font-black text-espresso">
                                    {
                                        ar
                                            ? "3. \u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0646\u0634\u0627\u0637"
                                            : "3. Business details"
                                    }
                                </h2>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0627\u0633\u0645 \u0627\u0644\u0645\u0646\u0634\u0623\u0629 / \u0627\u0644\u0645\u0634\u0631\u0648\u0639"
                                                    : "Business / project name"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.business_name
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "business_name",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>

                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0627\u0644\u0646\u0634\u0627\u0637"
                                                    : "Activity"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.activity_name
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "activity_name",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>

                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0627\u0644\u0645\u062f\u064a\u0646\u0629"
                                                    : "City"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.city
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "city",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>

                                    <div>
                                        <Label>
                                            {
                                                businessNumberLabel
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.business_document_number
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "business_document_number",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>
                                </div>

                                <UploadField
                                    field="business_document_file_id"
                                    label={
                                        ar
                                            ? "\u0631\u0641\u0639 \u0648\u062b\u064a\u0642\u0629 \u0627\u0644\u0646\u0634\u0627\u0637"
                                            : "Business document"
                                    }
                                />
                            </section>


                            <section className="space-y-4">
                                <h2 className="text-lg font-black text-espresso">
                                    {
                                        ar
                                            ? "4. \u0636\u0631\u064a\u0628\u0629 \u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629"
                                            : "4. VAT"
                                    }
                                </h2>

                                <Label>
                                    {
                                        ar
                                            ? "\u0647\u0644 \u0623\u0646\u062a \u0645\u0633\u062c\u0644 \u0641\u064a \u0636\u0631\u064a\u0628\u0629 \u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629\u061f"
                                            : "Are you VAT registered?"
                                    }
                                </Label>

                                <RadioGroup
                                    value={
                                        form.vat_registered
                                            ? "yes"
                                            : "no"
                                    }
                                    onValueChange={
                                        (value) =>
                                            setValue(
                                                "vat_registered",
                                                value === "yes"
                                            )
                                    }
                                    className="flex gap-3"
                                >
                                    <label className="flex-1 p-3 rounded-xl border-2 border-border cursor-pointer">
                                        <RadioGroupItem
                                            value="no"
                                            className="me-2"
                                        />

                                        {
                                            ar
                                                ? "\u0644\u0627"
                                                : "No"
                                        }
                                    </label>

                                    <label className="flex-1 p-3 rounded-xl border-2 border-border cursor-pointer">
                                        <RadioGroupItem
                                            value="yes"
                                            className="me-2"
                                        />

                                        {
                                            ar
                                                ? "\u0646\u0639\u0645"
                                                : "Yes"
                                        }
                                    </label>
                                </RadioGroup>

                                {
                                    form.vat_registered
                                    && (
                                        <>
                                            <div>
                                                <Label>
                                                    {
                                                        ar
                                                            ? "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0636\u0631\u064a\u0628\u064a"
                                                            : "VAT number"
                                                    }
                                                    {" *"}
                                                </Label>

                                                <Input
                                                    required
                                                    value={
                                                        form.vat_number
                                                    }
                                                    onChange={
                                                        (event) =>
                                                            setValue(
                                                                "vat_number",
                                                                event.target.value
                                                            )
                                                    }
                                                    className="mt-1.5"
                                                />
                                            </div>

                                            <UploadField
                                                field="vat_certificate_file_id"
                                                label={
                                                    ar
                                                        ? "\u0634\u0647\u0627\u062f\u0629 \u0627\u0644\u0636\u0631\u064a\u0628\u0629"
                                                        : "VAT certificate"
                                                }
                                            />
                                        </>
                                    )
                                }
                            </section>


                            <section className="space-y-4">
                                <h2 className="text-lg font-black text-espresso">
                                    {
                                        ar
                                            ? "5. \u0627\u0644\u062d\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064a"
                                            : "5. Bank account"
                                    }
                                </h2>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0627\u0633\u0645 \u0627\u0644\u0628\u0646\u0643"
                                                    : "Bank name"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.bank_name
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "bank_name",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>

                                    <div>
                                        <Label>
                                            {
                                                ar
                                                    ? "\u0627\u0633\u0645 \u0635\u0627\u062d\u0628 \u0627\u0644\u062d\u0633\u0627\u0628"
                                                    : "Account holder name"
                                            }
                                            {" *"}
                                        </Label>

                                        <Input
                                            required
                                            value={
                                                form.bank_account_name
                                            }
                                            onChange={
                                                (event) =>
                                                    setValue(
                                                        "bank_account_name",
                                                        event.target.value
                                                    )
                                            }
                                            className="mt-1.5"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label>
                                        IBAN *
                                    </Label>

                                    <Input
                                        required
                                        dir="ltr"
                                        placeholder="SA0000000000000000000000"
                                        value={
                                            form.iban
                                        }
                                        onChange={
                                            (event) =>
                                                setValue(
                                                    "iban",
                                                    event.target.value
                                                )
                                        }
                                        className="mt-1.5"
                                    />

                                    <p className="text-xs text-muted-foreground mt-1">
                                        {
                                            ar
                                                ? "\u064a\u062c\u0628 \u0623\u0646 \u064a\u0628\u062f\u0623 \u0627\u0644\u0622\u064a\u0628\u0627\u0646 \u0627\u0644\u0633\u0639\u0648\u062f\u064a \u0628\u0640 SA."
                                                : "Saudi IBAN must start with SA."
                                        }
                                    </p>
                                </div>

                                <UploadField
                                    field="iban_certificate_file_id"
                                    label={
                                        ar
                                            ? "\u0634\u0647\u0627\u062f\u0629 / \u062e\u0637\u0627\u0628 IBAN"
                                            : "IBAN certificate / letter"
                                    }
                                />
                            </section>


                            <div className="bg-surface rounded-2xl p-4 text-sm text-muted-foreground">
                                {
                                    ar
                                        ? "\u0628\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0637\u0644\u0628 \u0641\u0625\u0646\u0643 \u062a\u0642\u0631 \u0628\u0635\u062d\u0629 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a \u0648\u0627\u0644\u0645\u0633\u062a\u0646\u062f\u0627\u062a \u0627\u0644\u0645\u0631\u0641\u0642\u0629."
                                        : "By submitting, you confirm that the information and documents provided are accurate."
                                }
                            </div>


                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full rounded-full h-12 bg-terracotta text-white font-bold"
                                data-testid="submit-seller-application"
                            >
                                {
                                    submitting
                                        ? "..."
                                        : (
                                            ar
                                                ? "\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0637\u0644\u0628 \u0644\u0644\u0645\u0631\u0627\u062c\u0639\u0629"
                                                : "Submit for review"
                                        )
                                }
                            </Button>

                        </form>
                    )
                }

            </div>
        </Layout>
    );
}
